import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { getTeams, deleteTeam } from '../../redux/features/teamSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { fetchArchiveJobs } from '../../redux/features/jobSlice';
import { formatShareLabel } from '../../utils/teamShareUtils';
import { formatMonthKey, parseMonthKey } from '../../utils/workerStatsUtils';
import { loadRatingParams } from '../../utils/workerRatingUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import TeamForm from './TeamForm';
import TeamShareFormulaPanel from './TeamShareFormulaPanel';
import TeamStatsCard from './TeamStatsCard';

const TeamList = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [archiveYear, setArchiveYear] = useState(new Date().getFullYear());
  const [teamMonthKeys, setTeamMonthKeys] = useState({});
  const [ratingParams] = useState(loadRatingParams);

  const { teams, loading } = useSelector((state) => state.team || { teams: [], loading: false });
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { archiveJobs } = useSelector((state) => state.job || { archiveJobs: [] });
  const { user } = useSelector((state) => state.auth || {});

  const getTeamMonthKey = (teamId) =>
    teamMonthKeys[teamId] || formatMonthKey(new Date().getFullYear(), new Date().getMonth());

  const handleTeamMonthChange = (teamId, monthKey) => {
    setTeamMonthKeys((prev) => ({ ...prev, [teamId]: monthKey }));
    const { year } = parseMonthKey(monthKey);
    if (year !== archiveYear) setArchiveYear(year);
  };

  useEffect(() => {
    dispatch(getTeams());
    dispatch(getWorkers());
  }, [dispatch]);

  useEffect(() => {
    const businessType = user?.result?.businessType || getBusinessType() || 'Home Appliance Technician';
    const start = new Date(archiveYear, 0, 1, 0, 0, 0, 0);
    const end = new Date(archiveYear, 11, 31, 23, 59, 59, 999);
    dispatch(
      fetchArchiveJobs({
        businessType,
        startDate: start.toISOString(),
        endDate: end.toISOString()
      })
    );
  }, [dispatch, user, archiveYear]);

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedTeam(null);
    setShowModal(true);
  };

  const handleEdit = (team) => {
    setIsEdit(true);
    setSelectedTeam(team);
    setShowModal(true);
  };

  const handleDelete = async (teamId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj tim?')) {
      try {
        await dispatch(deleteTeam({ id: teamId })).unwrap();
        toast.success('Tim je uspešno obrisan');
      } catch (error) {
        toast.error(error?.message || 'Greška pri brisanju tima');
      }
    }
  };

  const getMemberSharesDisplay = (team) => {
    const shares = team.memberShares;
    if (!shares || shares.length === 0) return null;

    return shares
      .map((entry) => {
        const workerRef = entry.worker;
        let name = '';
        if (workerRef && typeof workerRef === 'object' && workerRef.firstName) {
          name = `${workerRef.firstName} ${workerRef.lastName}`.trim();
        } else {
          const workerId = workerRef?._id || workerRef;
          const worker = workers.find((w) => w._id === workerId);
          name = worker ? `${worker.firstName} ${worker.lastName}` : '';
        }
        if (!name) return null;
        const spec = entry.specialization ? formatShareLabel(entry.specialization) : '';
        return `${name} ${entry.sharePercent}%${spec ? ` (${spec})` : ''}`;
      })
      .filter(Boolean)
      .join(' · ');
  };

  const getWorkerNames = (members) => {
    if (!members || !Array.isArray(members) || members.length === 0) {
      return 'Nema dodeljenih radnika';
    }

    const memberNames = members
      .map((member) => {
        if (!member) return null;

        if (typeof member === 'object') {
          if (member.firstName || member.lastName) {
            return `${member.firstName || ''} ${member.lastName || ''}`.trim();
          }

          if (member._id) {
            const worker = workers.find((w) => w._id === member._id);
            if (worker) {
              return `${worker.firstName} ${worker.lastName}`;
            }
          }
          return null;
        }

        const worker = workers.find((w) => w._id === member);
        return worker ? `${worker.firstName} ${worker.lastName}` : null;
      })
      .filter(Boolean);

    return memberNames.length > 0 ? memberNames.join(', ') : 'Nema dodeljenih radnika';
  };

  const renderModal = () =>
    showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-4">
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <TeamForm
            isEdit={isEdit}
            team={selectedTeam}
            onClose={() => setShowModal(false)}
            availableWorkers={workers}
          />
        </div>
      </div>
    );

  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Timovi</h2>
              <p className="text-xs text-gray-400 mt-1">
                Mesečni pregled po kartici · statistika = zajednički poslovi tima
              </p>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Dodaj tim</span>
              <span className="sm:hidden">Dodaj</span>
            </button>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 sm:p-8 text-center text-gray-400 shadow-lg mb-4 sm:mb-6">
          Nema timova. Dodajte svoj prvi tim da počnete.
        </div>

        <TeamShareFormulaPanel />
        {renderModal()}
      </div>
    );
  }

  return (
    <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
        <div className="flex justify-between items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Timovi</h2>
            <p className="text-xs text-gray-400 mt-1">
              Mesečni pregled po kartici · statistika = zajednički poslovi tima
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Dodaj tim</span>
            <span className="sm:hidden">Dodaj</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-4">
        {teams.map((team) => (
          <TeamStatsCard
            key={team._id}
            team={team}
            workers={workers}
            archiveJobs={archiveJobs}
            monthKey={getTeamMonthKey(team._id)}
            onMonthChange={(key) => handleTeamMonthChange(team._id, key)}
            ratingParams={ratingParams}
            getMemberNames={getWorkerNames}
            getMemberSharesDisplay={getMemberSharesDisplay}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </div>

      <TeamShareFormulaPanel />
      {renderModal()}
    </div>
  );
};

export default TeamList;
